import type { GroupRef, MakerAgent, OwnerRef, OwnerView } from '@ralph/contracts'
import { ArrowRightLeft, Info } from 'lucide-react'
import { useId, useState } from 'react'
import { type OwnerChoice, OwnerPicker, ownerRefOf } from '@/components/groups/OwnerPicker.tsx'
import { OwnerLabel } from '@/components/marketplace/consumer/OwnerLabel.tsx'
import { Note } from '@/components/marketplace/maker/BuilderShell.tsx'
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/Dialog.tsx'
import { Button, Spinner } from '@/components/ui/primitives.tsx'
import { useMoveAgent } from '@/hooks/use-maker.ts'
import { describeApiError, useT } from '@/i18n/index.ts'

export type OwnerLineViewer = { id: string; displayName: string; isAdmin: boolean; managedGroups: readonly GroupRef[] }

export const ownerRefOfView = (owner: OwnerView): OwnerRef =>
  owner.kind === 'user' ? { kind: 'user', id: owner.id } : { kind: 'group', id: owner.group.id }

/**
 * "Owned by ⬤ Finance EMEA · Move to a group…" (PRD-26 D-19, D-32), under the builder's title.
 *
 * The move is offered only where it can succeed: to the agent's maintainers when they manage a
 * group other than the one that owns it, and to administrators, who may move it anywhere. The
 * dialog says the two things everyone moving something asks — who can use it does not change, and
 * the destination's limits apply — and shows the server's own sentence when a limit refuses it.
 */
export const OwnerLine = ({
  agent,
  viewer,
  canMove
}: {
  agent: MakerAgent
  viewer: OwnerLineViewer
  /** Its maintainers and administrators; the server decides where they may move it. */
  canMove: boolean
}) => {
  // In the body, never at module scope (PRD-27 F-3). The dialog below reads its own, for the
  // same reason: a branch shared through a module-level const is a capture either way.
  const strings = useT().marketplace.maker.owner
  const [moving, setMoving] = useState(false)
  const current = ownerRefOfView(agent.owner)
  const destinations = viewer.managedGroups.filter((group) => !(current.kind === 'group' && current.id === group.id))
  const offered = canMove && (viewer.isAdmin || destinations.length > 0)

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
      <span className="eyebrow shrink-0">{strings.label}</span>
      <OwnerLabel owner={agent.owner} size={20} to className="font-medium" />
      {offered && (
        <>
          <span aria-hidden className="text-fg-subtle">
            ·
          </span>
          <button
            type="button"
            onClick={() => setMoving(true)}
            className="rounded-chip text-fg-muted underline-offset-2 transition-colors hover:text-fg hover:underline"
          >
            {viewer.isAdmin ? strings.moveAnywhere : strings.move}
          </button>
        </>
      )}

      {moving && <MoveDialog agent={agent} viewer={viewer} current={current} onClose={() => setMoving(false)} />}
    </div>
  )
}

const MoveDialog = ({
  agent,
  viewer,
  current,
  onClose
}: {
  agent: MakerAgent
  viewer: OwnerLineViewer
  current: OwnerRef
  onClose: () => void
}) => {
  // The whole catalogue, not only this surface's slice: `describeApiError` reads `errors` and
  // `common` as well, and the refusal below is one of theirs.
  const messages = useT()
  const strings = messages.marketplace.maker.owner
  const titleId = useId()
  const [choice, setChoice] = useState<OwnerChoice | null>(null)
  const move = useMoveAgent(agent.agentId)

  const submit = () => {
    if (!choice) return
    move.mutate(ownerRefOf(choice), { onSuccess: onClose })
  }

  return (
    <Dialog
      titleId={titleId}
      icon={<ArrowRightLeft size={14} strokeWidth={2.1} />}
      title={strings.moveTitle(agent.displayName)}
      lead={strings.moveLead}
      onClose={onClose}
    >
      <DialogBody>
        <OwnerPicker
          value={choice}
          onChange={setChoice}
          viewer={{ id: viewer.id, displayName: viewer.displayName, managedGroups: viewer.managedGroups }}
          current={current}
          allowAnyone={viewer.isAdmin}
          includeSelf={viewer.isAdmin}
          disabled={move.isPending}
        />
        {!viewer.isAdmin && <p className="text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.moveRule}</p>}
        <Note tone="info" icon={<Info size={13} strokeWidth={2} />}>
          {strings.moveKeeps}
        </Note>
        {/*
          One whole sentence, rather than this surface's "that could not be moved" with the
          server's English appended: a localized fragment joined to a foreign one cannot agree in
          any language. The refusal that matters here is the server's — it names the rule, "you
          can move a knowledge base only to a group you manage" — and the catalogue now holds it
          under a key (PRD-27 D-4, LOC-033).
        */}
        {move.isError && (
          <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
            {describeApiError(messages, move.error)}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {strings.cancel}
        </Button>
        <Button
          variant="primary"
          icon={move.isPending ? <Spinner size={13} /> : undefined}
          disabled={choice === null || move.isPending}
          onClick={submit}
        >
          {move.isPending ? strings.moving : strings.moveSubmit}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
